import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import useAuthenticate from "../../../hooks/useAuthenticate";
import Navbar from "src/components/organisms/navbar";
import {
  PageLocationTitle,
  SummaryCard,
  SummaryValue,
  SummaryLabel,
  ChartContainer,
} from "./styles";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";

interface Tournament {
  id: number;
  name: string;
  date: string;
  status: boolean;
  slug: string;
  taxaPlataforma: number | null;
  taxaPagbank: number | null;
  valorCadastroEvento: number | null;
  valorAcompanhamento: number | null;
}

interface TournamentPaymentInfo {
  tournamentId: number;
  resultManual: { value: string; occurrences: number }[];
  resultAuto: { value: string; occurrences: number }[];
  totalPaid: number;
  totalUnpaid: number;
}

interface TournamentShirtsInfo {
  tournamentId: number;
  paidTotal: number;
  paidCount: number;
  pendingTotal: number;
  pendingCount: number;
}

interface MergedTournament extends Tournament {
  receitaManual: number;
  receitaAuto: number;
  receitaBruta: number;
  totalPaid: number;
  totalUnpaid: number;
  taxaPlataformaCalc: number;
  taxaPagbankCalc: number;
  lucro: number;
  receitaCamisasPagas: number;
  receitaCamisasPendentes: number;
  qtdCamisasPagas: number;
  qtdCamisasPendentes: number;
}

const calcTotal = (items: { value: string; occurrences: number }[]) =>
  items.reduce((acc, item) => acc + (parseFloat(item.value) || 0) * item.occurrences, 0);

const formatBRL = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

const COLORS = ["#1a73e8", "#218802", "#ff9800", "#ac0000"];

const FinancialDashboard = () => {
  const navigate = useNavigate();
  const { getToken } = useAuthenticate();
  const [loading, setLoading] = useState(true);
  const [merged, setMerged] = useState<MergedTournament[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" }>({
    key: "receitaBruta",
    direction: "desc",
  });
  const [editOpen, setEditOpen] = useState(false);
  const [editTournament, setEditTournament] = useState<MergedTournament | null>(null);
  const [editFields, setEditFields] = useState({
    taxaPlataforma: "",
    taxaPagbank: "",
    valorCadastroEvento: "",
    valorAcompanhamento: "",
  });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${getToken() || ""}` };
      const [tournamentsRes, paymentsRes, shirtsRes] = await Promise.all([
        api.get("/managers/tournaments/info", { headers }),
        api.get("/administrators/tournaments/payment/infos-by-tournament", { headers }),
        api.get("/administrators/tournaments/thisrts/all", { headers }),
      ]);

      const tournaments: Tournament[] = tournamentsRes.data;
      const payments: TournamentPaymentInfo[] = paymentsRes.data;
      const shirts: TournamentShirtsInfo[] = shirtsRes.data;
      const paymentMap = new Map(payments.map((p) => [p.tournamentId, p]));
      const shirtsMap = new Map(shirts.map((s) => [s.tournamentId, s]));

      const mergedData: MergedTournament[] = tournaments.map((t) => {
        const pay = paymentMap.get(t.id);
        const shirt = shirtsMap.get(t.id);
        const receitaManual = pay ? calcTotal(pay.resultManual) : 0;
        const receitaAuto = pay ? calcTotal(pay.resultAuto) : 0;
        const receitaBruta = receitaManual + receitaAuto;
        const txPlat = Number(t.taxaPlataforma) || 3;
        const txPB = Number(t.taxaPagbank) || 0.3;
        const cadEvento = Number(t.valorCadastroEvento) || 0;
        const acomp = Number(t.valorAcompanhamento) || 0;
        const taxaPlataformaCalc = receitaAuto * (txPlat / 100);
        const taxaPagbankCalc = receitaAuto * (txPB / 100);
        const lucro = taxaPlataformaCalc - taxaPagbankCalc + cadEvento + acomp;

        return {
          ...t,
          taxaPlataforma: txPlat,
          taxaPagbank: txPB,
          valorCadastroEvento: cadEvento,
          valorAcompanhamento: acomp,
          receitaManual,
          receitaAuto,
          receitaBruta,
          totalPaid: pay?.totalPaid || 0,
          totalUnpaid: pay?.totalUnpaid || 0,
          taxaPlataformaCalc,
          taxaPagbankCalc,
          lucro,
          receitaCamisasPagas: shirt?.paidTotal || 0,
          receitaCamisasPendentes: shirt?.pendingTotal || 0,
          qtdCamisasPagas: shirt?.paidCount || 0,
          qtdCamisasPendentes: shirt?.pendingCount || 0,
        };
      });

      setMerged(mergedData);
    } catch {
      toast.error("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const filtered = merged.filter((t) => {
    if (!t.date) return !dateFrom && !dateTo;
    const d = new Date(t.date).getTime();
    if (dateFrom && d < new Date(dateFrom).getTime()) return false;
    if (dateTo && d > new Date(dateTo + "T23:59:59").getTime()) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const aVal = (a as any)[sortConfig.key];
    const bVal = (b as any)[sortConfig.key];
    if (typeof aVal === "string") return sortConfig.direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
  });

  const totalReceitaBruta = filtered.reduce((acc, t) => acc + t.receitaBruta, 0);
  const totalLucro = filtered.reduce((acc, t) => acc + t.lucro, 0);
  const totalInscritos = filtered.reduce((acc, t) => acc + t.totalPaid + t.totalUnpaid, 0);
  const totalReceitaCamisas = filtered.reduce((acc, t) => acc + t.receitaCamisasPagas, 0);
  const totalQtdCamisas = filtered.reduce((acc, t) => acc + t.qtdCamisasPagas, 0);

  const barData = filtered
    .filter((t) => t.receitaBruta > 0)
    .sort((a, b) => b.receitaBruta - a.receitaBruta)
    .map((t) => ({ name: t.name.length > 25 ? t.name.slice(0, 25) + "…" : t.name, receita: t.receitaBruta }));

  const lineData = (() => {
    const s = [...filtered]
      .filter((t) => t.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let acumulado = 0;
    return s.map((t) => {
      acumulado += t.receitaBruta;
      return {
        date: new Date(t.date).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }),
        receita: acumulado,
      };
    });
  })();

  const totalManual = filtered.reduce((acc, t) => acc + t.receitaManual, 0);
  const totalAuto = filtered.reduce((acc, t) => acc + t.receitaAuto, 0);
  const pieData = [
    { name: "Manual", value: totalManual },
    { name: "Automático", value: totalAuto },
  ];

  const lucroBarData = filtered
    .filter((t) => t.lucro !== 0)
    .sort((a, b) => b.lucro - a.lucro)
    .map((t) => ({ name: t.name.length > 25 ? t.name.slice(0, 25) + "…" : t.name, lucro: t.lucro }));

  const lucroAcumuladoData = (() => {
    const s = [...filtered]
      .filter((t) => t.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    let acumulado = 0;
    return s.map((t) => {
      acumulado += t.lucro;
      return {
        date: new Date(t.date).toLocaleDateString("pt-BR", { month: "short", year: "2-digit" }),
        acumulado,
      };
    });
  })();

  const handleOpenEdit = (t: MergedTournament) => {
    setEditTournament(t);
    setEditFields({
      taxaPlataforma: String(t.taxaPlataforma ?? "3"),
      taxaPagbank: String(t.taxaPagbank ?? "0.3"),
      valorCadastroEvento: String(t.valorCadastroEvento ?? "0"),
      valorAcompanhamento: String(t.valorAcompanhamento ?? "0"),
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editTournament) return;
    try {
      await api.patch(
        `/managers/tournaments/${editTournament.id}/financial`,
        {
          taxaPlataforma: Number(editFields.taxaPlataforma.replace(",", ".")),
          taxaPagbank: Number(editFields.taxaPagbank.replace(",", ".")),
          valorCadastroEvento: Number(editFields.valorCadastroEvento.replace(",", ".")),
          valorAcompanhamento: Number(editFields.valorAcompanhamento.replace(",", ".")),
        },
        { headers: { Authorization: `Bearer ${getToken() || ""}` } }
      );
      toast.success("Dados financeiros atualizados");
      setEditOpen(false);
      await loadData();
    } catch {
      toast.error("Erro ao salvar dados financeiros");
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <PageLocationTitle>Dashboard Financeiro</PageLocationTitle>
          <Button variant="outlined" size="small" onClick={() => navigate("/admin/payment-management")}>
            Ver por Torneio
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3, flexWrap: "wrap" }}>
          <TextField
            label="Data início"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
          <TextField
            label="Data fim"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 170 }}
          />
          {(dateFrom || dateTo) && (
            <Button variant="text" size="small" onClick={() => { setDateFrom(""); setDateTo(""); }}>
              Limpar filtro
            </Button>
          )}
          <Typography variant="body2" color="textSecondary">
            {filtered.length} de {merged.length} torneio(s)
          </Typography>
        </Box>

        {/* Cards resumo */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr 1fr" }}
          gap={2}
          sx={{ mb: 3 }}
        >
          <SummaryCard>
            <Box display="flex" flexDirection="column">
              <SummaryLabel>Total de Torneios</SummaryLabel>
              <SummaryValue translate="no">{merged.length}</SummaryValue>
            </Box>
          </SummaryCard>
          <SummaryCard>
            <Box display="flex" flexDirection="column">
              <SummaryLabel>Receita Total</SummaryLabel>
              <SummaryValue translate="no" style={{ color: "#1a73e8" }}>
                {formatBRL(totalReceitaBruta)}
              </SummaryValue>
            </Box>
          </SummaryCard>
          <SummaryCard>
            <Box display="flex" flexDirection="column">
              <SummaryLabel>Lucro Total da Plataforma</SummaryLabel>
              <SummaryValue translate="no" style={{ color: "#218802" }}>
                {formatBRL(totalLucro)}
              </SummaryValue>
            </Box>
          </SummaryCard>
          <SummaryCard>
            <Box display="flex" flexDirection="column">
              <SummaryLabel>Total de Inscritos</SummaryLabel>
              <SummaryValue translate="no">{totalInscritos}</SummaryValue>
            </Box>
          </SummaryCard>
          <SummaryCard>
            <Box display="flex" flexDirection="column">
              <SummaryLabel>Receita Camisas</SummaryLabel>
              <SummaryValue translate="no" style={{ color: "#9c27b0" }}>
                {formatBRL(totalReceitaCamisas)}
              </SummaryValue>
              <Typography variant="caption" color="textSecondary">
                <span>{totalQtdCamisas}</span> camisa(s) paga(s)
              </Typography>
            </Box>
          </SummaryCard>
        </Box>

        {/* Gráficos */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
          gap={2}
          sx={{ mb: 3 }}
        >
          <ChartContainer>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Receita por Torneio
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tick={false} axisLine={false} />
                <YAxis type="category" dataKey="name" tick={false} axisLine={false} width={10} />
                <Tooltip formatter={(v: number | undefined) => formatBRL(v ?? 0)} />
                <Bar dataKey="receita" fill="#1a73e8" name="Receita" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>

          <ChartContainer>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Evolução da Receita Acumulada
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={lineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={false} axisLine={false} />
                <YAxis tick={false} axisLine={false} />
                <Tooltip formatter={(v: number | undefined) => formatBRL(v ?? 0)} />
                <Area type="monotone" dataKey="receita" stroke="#1a73e8" fill="#1a73e833" name="Receita Acumulada" strokeWidth={2} dot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>

          <ChartContainer>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Distribuição Manual vs Automático
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100}>
                  {pieData.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number | undefined) => formatBRL(v ?? 0)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>

          <ChartContainer>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Lucro por Torneio
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={lucroBarData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={false} axisLine={false} />
                <YAxis tick={false} axisLine={false} />
                <Tooltip formatter={(v: number | undefined) => formatBRL(v ?? 0)} />
                <Bar dataKey="lucro" fill="#ff9800" name="Lucro" />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>

          <ChartContainer style={{ gridColumn: "1 / -1" }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Evolução do Lucro Acumulado
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={lucroAcumuladoData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={false} axisLine={false} />
                <YAxis tick={false} axisLine={false} />
                <Tooltip formatter={(v: number | undefined) => formatBRL(v ?? 0)} />
                <Area type="monotone" dataKey="acumulado" stroke="#218802" fill="#21880233" name="Lucro Acumulado" strokeWidth={2} dot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Box>

        {/* Tabela detalhada */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => {
              const rows = sorted.map((t) => ({
                ID: t.id,
                Nome: t.name,
                Data: t.date ? new Date(t.date).toLocaleDateString("pt-BR") : "-",
                "Receita Bruta": t.receitaBruta,
                "Receita Líquida": t.receitaBruta - t.taxaPlataformaCalc,
                Pagos: t.totalPaid,
                "Não Pagos": t.totalUnpaid,
                "Camisas (Qtd)": t.qtdCamisasPagas,
                "Receita Camisas": t.receitaCamisasPagas,
                "Taxa Plataforma (%)": t.taxaPlataforma,
                "Taxa PagBank (%)": t.taxaPagbank,
                "Cadastro Evento": t.valorCadastroEvento,
                Acompanhamento: t.valorAcompanhamento,
                Lucro: t.lucro,
              }));
              const ws = XLSX.utils.json_to_sheet(rows);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, "Dashboard Financeiro");
              XLSX.writeFile(wb, "dashboard-financeiro.xlsx");
            }}
          >
            Exportar XLS
          </Button>
        </Box>
        <TableContainer component={Paper} sx={{ mb: 3, overflowX: "hidden" }}>
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 50 }}>
                  <TableSortLabel active={sortConfig.key === "id"} direction={sortConfig.key === "id" ? sortConfig.direction : "asc"} onClick={() => handleSort("id")}>
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: "18%" }}>
                  <TableSortLabel active={sortConfig.key === "name"} direction={sortConfig.key === "name" ? sortConfig.direction : "asc"} onClick={() => handleSort("name")}>
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 80 }}>
                  <TableSortLabel active={sortConfig.key === "date"} direction={sortConfig.key === "date" ? sortConfig.direction : "asc"} onClick={() => handleSort("date")}>
                    Data
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 90 }}>
                  <TableSortLabel active={sortConfig.key === "receitaBruta"} direction={sortConfig.key === "receitaBruta" ? sortConfig.direction : "asc"} onClick={() => handleSort("receitaBruta")}>
                    Rec. Bruta
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 90 }}>Rec. Liq.</TableCell>
                <TableCell align="right" sx={{ width: 55 }}>
                  <TableSortLabel active={sortConfig.key === "totalPaid"} direction={sortConfig.key === "totalPaid" ? sortConfig.direction : "asc"} onClick={() => handleSort("totalPaid")}>
                    Pagos
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 65 }}>
                  <TableSortLabel active={sortConfig.key === "totalUnpaid"} direction={sortConfig.key === "totalUnpaid" ? sortConfig.direction : "asc"} onClick={() => handleSort("totalUnpaid")}>
                    N. Pagos
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 80 }}>
                  <TableSortLabel active={sortConfig.key === "receitaCamisasPagas"} direction={sortConfig.key === "receitaCamisasPagas" ? sortConfig.direction : "asc"} onClick={() => handleSort("receitaCamisasPagas")}>
                    Camisas
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: 60 }}>Tx Plat.(%)</TableCell>
                <TableCell align="right" sx={{ width: 60 }}>Tx PB(%)</TableCell>
                <TableCell align="right" sx={{ width: 75 }}>Cad. Evento</TableCell>
                <TableCell align="right" sx={{ width: 75 }}>Acomp.</TableCell>
                <TableCell align="right" sx={{ width: 85 }}>
                  <TableSortLabel active={sortConfig.key === "lucro"} direction={sortConfig.key === "lucro" ? sortConfig.direction : "asc"} onClick={() => handleSort("lucro")}>
                    Lucro
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ width: 50 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((t) => (
                <TableRow key={t.id}>
                  <TableCell translate="no">{t.id}</TableCell>
                  <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</TableCell>
                  <TableCell translate="no">{t.date ? new Date(t.date).toLocaleDateString("pt-BR") : "-"}</TableCell>
                  <TableCell align="right" translate="no">{formatBRL(t.receitaBruta)}</TableCell>
                  <TableCell align="right" translate="no">{formatBRL(t.receitaBruta - t.taxaPlataformaCalc)}</TableCell>
                  <TableCell align="right" translate="no">{t.totalPaid}</TableCell>
                  <TableCell align="right" translate="no">{t.totalUnpaid}</TableCell>
                  <TableCell align="right" translate="no" style={{ color: t.receitaCamisasPagas > 0 ? "#9c27b0" : undefined }}>
                    {t.receitaCamisasPagas > 0 ? `${formatBRL(t.receitaCamisasPagas)} (${t.qtdCamisasPagas})` : "-"}
                  </TableCell>
                  <TableCell align="right" translate="no">{t.taxaPlataforma ?? 3}%</TableCell>
                  <TableCell align="right" translate="no">{t.taxaPagbank ?? 0.3}%</TableCell>
                  <TableCell align="right" translate="no">{formatBRL(t.valorCadastroEvento || 0)}</TableCell>
                  <TableCell align="right" translate="no">{formatBRL(t.valorAcompanhamento || 0)}</TableCell>
                  <TableCell align="right" translate="no" style={{ color: t.lucro >= 0 ? "#218802" : "#ac0000", fontWeight: 600 }}>
                    {formatBRL(t.lucro)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenEdit(t)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Modal de edição */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Editar Dados Financeiros</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              <TextField
                label="Taxa Plataforma (%)"
                value={editFields.taxaPlataforma}
                onChange={(e) => setEditFields((f) => ({ ...f, taxaPlataforma: e.target.value }))}
                size="small"
              />
              <TextField
                label="Taxa PagBank (%)"
                value={editFields.taxaPagbank}
                onChange={(e) => setEditFields((f) => ({ ...f, taxaPagbank: e.target.value }))}
                size="small"
              />
              <TextField
                label="Cadastro Evento (R$)"
                value={editFields.valorCadastroEvento}
                onChange={(e) => setEditFields((f) => ({ ...f, valorCadastroEvento: e.target.value }))}
                size="small"
              />
              <TextField
                label="Acompanhamento (R$)"
                value={editFields.valorAcompanhamento}
                onChange={(e) => setEditFields((f) => ({ ...f, valorAcompanhamento: e.target.value }))}
                size="small"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave}>Salvar</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default FinancialDashboard;
